<template>
  <CModal
    :show.sync="isVisible"
    :close-on-backdrop="false"
    size="xl"
    @update:show="handleClose"
  >
    <template #header>
      <h5 class="modal-title">
        <CIcon name="cipSend" size="sm" class="mr-2"/>
        <translate translate-context="collaborators.invite">Invite Collaborators</translate>
      </h5>
      <button type="button" class="close" @click="close">
        <span>&times;</span>
      </button>
    </template>

    <div class="px-3 py-2">
      <!-- Description -->
      <div class="alert alert-primary" role="alert">
        <strong><translate translate-context="collaborators.invite">Send Invitation Email</translate></strong>
        <br>
        <translate translate-context="collaborators.invite">Invited users will receive an email with instructions to accept the invitation and access the platform.</translate>
      </div>

      <!-- Email field -->
      <div class="mb-3">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <label class="font-weight-bold mb-0">
            <translate translate-context="collaborators.invite">Email addresses</translate>
          </label>
          <span v-if="emails.length > 0" class="badge badge-success">
            {{ emails.length }} <translate translate-context="collaborators.invite">email(s)</translate>
          </span>
        </div>

        <CInput
          ref="emailInput"
          v-model="currentInput"
          type="text"
          :placeholder="$gettext('name@example.com (press Enter to add)')"
          @keydown.enter.prevent="handleAddEmail"
        />

        <div v-if="emailError" class="text-danger small mt-1">
          {{ emailError }}
        </div>

        <!-- Emails List -->
        <div v-if="emails.length > 0" class="mt-3">
          <div class="d-flex flex-wrap gap-2">
            <span
              v-for="(email, index) in emails"
              :key="index"
              class="badge badge-primary px-3 py-2"
              style="font-size: 14px; cursor: pointer;"
              @click="removeEmail(index)"
            >
              {{ email }} <CIcon name="cilX" size="sm" class="ml-1"/>
            </span>
          </div>
        </div>
      </div>

      <!-- Roles Selection -->
      <div class="mb-3">
        <label class="font-weight-bold">
          <translate translate-context="collaborators.invite">Roles</translate>
        </label>
        <RoleGrid
          v-model="selectedRoles"
          :roles="availableRoles"
          @show-permissions="handleShowPermissions"
        />
      </div>

      <!-- Personal Message -->
      <div class="mb-3">
        <label class="font-weight-bold">
          <translate translate-context="collaborators.invite">Personal Message</translate>
          <span class="text-muted font-weight-normal small">
            <translate translate-context="collaborators.invite">(Optional)</translate>
          </span>
        </label>
        <CTextarea
          v-model="personalMessage"
          :placeholder="$gettext('Add a personal note for the invitation email...')"
          :maxlength="1000"
          rows="3"
        />
        <small class="text-muted">
          {{ personalMessage.length }}/1000
        </small>
      </div>
    </div>

    <template #footer>
      <CButton color="light" @click="close">
        <translate translate-context="collaborators.invite">Cancel</translate>
      </CButton>
      <CButton color="primary" @click="handleSubmit" :disabled="!canSubmit">
        <CIcon name="cipSend" size="sm" class="mr-1"/>
        <translate translate-context="collaborators.invite">Send Invitations</translate>
      </CButton>
    </template>

    <RolePermissionsModal
      ref="rolePermissionsModal"
      :scope="scope"
    />
  </CModal>
</template>

<script>
import {mapActions} from 'vuex'
import RoleGrid from './RoleGrid.vue'
import RolePermissionsModal from './RolePermissionsModal.vue'

export default {
  name: 'InviteCollaboratorModal',

  components: {
    RoleGrid,
    RolePermissionsModal
  },

  props: {
    scope: {
      type: Object,
      required: true
    },
    availableRoles: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      isVisible: false,
      emails: [],
      currentInput: '',
      selectedRoles: {},
      personalMessage: '',
      emailError: ''
    }
  },

  computed: {
    canSubmit() {
      const hasEmails = this.emails.length > 0
      const hasRoles = Object.values(this.selectedRoles).some(v => v)
      return hasEmails && hasRoles
    }
  },

  methods: {
    ...mapActions('collaborators', [
      'inviteCollaborators',
      'fetchCollaborators',
      'fetchRolePermissions'
    ]),

    open() {
      this.isVisible = true
      this.resetForm()
      this.$nextTick(() => {
        this.$refs.emailInput?.$el?.focus()
      })
    },

    close() {
      this.isVisible = false
    },

    handleClose(val) {
      if (!val) {
        this.close()
      }
    },

    resetForm() {
      this.emails = []
      this.currentInput = ''
      this.selectedRoles = {}
      this.personalMessage = ''
      this.emailError = ''
    },

    handleAddEmail() {
      const email = this.currentInput.trim().toLowerCase()
      this.emailError = ''

      if (!email) return

      // Validate email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(email)) {
        this.emailError = this.$pgettext('collaborators.notification', 'Invalid email format')
        return
      }

      // Check duplicates
      if (this.emails.includes(email)) {
        this.emailError = this.$pgettext('collaborators.notification', 'Email already added')
        return
      }

      this.emails.push(email)
      this.currentInput = ''
    },

    removeEmail(index) {
      this.emails.splice(index, 1)
    },

    async handleShowPermissions(role) {
      const permissions = await this.fetchRolePermissions(role.id)
      this.$refs.rolePermissionsModal.show(permissions, role)
    },

    async handleSubmit() {
      if (!this.canSubmit) return

      const roleIds = Object.keys(this.selectedRoles)
        .filter(id => this.selectedRoles[id])
        .map(id => ({id: parseInt(id)}))

      try {
        const result = await this.inviteCollaborators({
          users: this.emails,
          roles: roleIds,
          personalMessage: this.personalMessage || undefined
        })

        if (result.success && result.success.length > 0) {
          const successMsg = this.$pgettext('collaborators.notification', '{count} invitations sent successfully')
          await this.notifySuccess(successMsg.replace('{count}', result.success.length))
        }

        if (result.errors && Object.keys(result.errors).length > 0) {
          const errorMessages = Object.entries(result.errors)
            .map(([email, error]) => `${email}: ${error}`)
            .join('\n')
          await this.notifyError(
            this.$pgettext('collaborators.notification', 'Some operations failed:\n') + errorMessages
          )
        }

        this.close()
        await this.fetchCollaborators()
        this.$emit('invited')
      } catch (error) {
        await this.notifyError(this.$pgettext('collaborators.notification', 'Operation failed'))
      }
    }
  }
}
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}
</style>
